import type { ActionLogType } from '@/enums/ActionLogType'
import { buildQueryParams } from '@/helpers/ApiHelper'
import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { IPaginationOptions } from '@/misc/IPaginationOptions'
import type { IActionLogs } from '@/models/ActionLog/IActionLogs'
import type { IOkResponse } from '@/models/Responses/IOkResponse'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import { SERVICES_TYPES } from '../ServicesTypes'
import type IActionLogService from './IActionLogService'

@injectable()
export class ActionLogService implements IActionLogService {
  private readonly apiClient: IApiClient
  private readonly actionLogPrefix: string = 'actionlogs'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  async getLocalControllerActionLogsAsync(
    lcSerialNumber: string,
    pageNumber?: number,
    pageSize?: number,
    sortField?: string | null,
    isAscending?: boolean | null
  ): Promise<IPaginatedResponse<IActionLogs>> {
    const queryString = buildQueryParams({
      lcSerialNumber,
      pageNumber,
      pageSize,
      sortField,
      isAscending
    })

    return this.apiClient.get<IPaginatedResponse<IActionLogs>>(`${this.actionLogPrefix}${queryString}`)
  }

  async cancelLocalControllerScheduleRestart(actionLogId: string): Promise<IOkResponse> {
    return this.apiClient.put(`${this.actionLogPrefix}/local-controllers/cancel-restart/${actionLogId}`, {})
  }
  async cancelLocalControllerScheduleUpdate(actionLogId: string): Promise<IOkResponse> {
    return this.apiClient.put(`${this.actionLogPrefix}/local-controllers/cancel-update-firmware/${actionLogId}`, {})
  }
  async cancelLocalControllerScheduleConfig(actionLogId: string): Promise<IOkResponse> {
    return this.apiClient.put(`${this.actionLogPrefix}/local-controllers/cancel-configuration-apply/${actionLogId}`, {})
  }

  async getChargePointActionLogs(
    chargePointId: string,
    paginationOptions: IPaginationOptions,
    actionType?: ActionLogType,
    userName?: string | null,
    sortField?: string | null,
    isAscending?: boolean | null
  ): Promise<IPaginatedResponse<IActionLogs>> {
    const queryString = buildQueryParams({
      chargePointId,
      actionType,
      userName,
      pageNumber: paginationOptions.PageNumber,
      pageSize: paginationOptions.PageSize,
      sortField,
      isAscending
    })
    return this.apiClient.get(`${this.actionLogPrefix}${queryString}`)
  }

  async getAllActionLogs(
    paginationOptions: IPaginationOptions,
    actionType?: ActionLogType,
    userName?: string | null,
    sortField?: string | null,
    isAscending?: boolean | null
  ): Promise<IPaginatedResponse<IActionLogs>> {
    const queryString = buildQueryParams({
      actionType,
      userName,
      pageNumber: paginationOptions.PageNumber,
      pageSize: paginationOptions.PageSize,
      sortField,
      isAscending
    })
    return this.apiClient.get(`${this.actionLogPrefix}${queryString}`)
  }
}
